import { inject } from '@angular/core';
import {
  CanDeactivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { from, isObservable, Observable, of, switchMap } from 'rxjs';
import { CanComponentDeactivate } from './can-component-deactivate.interface';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { TranslateService } from '@ngx-translate/core';

type AnyRecord = Record<string, unknown>;

function readMaybeSignalValue(value: unknown): unknown {
  if (typeof value !== 'function') {
    return value;
  }

  try {
    return (value as () => unknown)();
  } catch {
    return value;
  }
}

function isTruthyFlag(component: AnyRecord, key: string): boolean {
  return Boolean(readMaybeSignalValue(component[key]));
}

function getCandidateForm(component: AnyRecord): AnyRecord | null {
  const candidates: unknown[] = [
    component['form'],
    (component['addFormComponent'] as AnyRecord | undefined)?.['form'],
    (component['addFormRef'] as AnyRecord | undefined)?.['form'],
    (component['addForm'] as AnyRecord | undefined)?.['form'],
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') {
      return candidate as AnyRecord;
    }
  }

  return null;
}

function showConfirmationDialog(): Observable<boolean> {
  const confirmService = inject(ConfirmationDialogService);
  const translationService = inject(TranslateService);

  return new Observable<boolean>((observer) => {
    confirmService.setConfirmationData(
      {
        header: translationService.instant(
          'common.component.can_deactivate.close_confirm_title'
        ),
        message: translationService.instant(
          'common.component.can_deactivate.close_confirm_msg'
        ),
        type: 'btnCancel',
      },
      () => {
        observer.next(true);
        observer.complete();
      },
      () => {
        observer.next(false);
        observer.complete();
      }
    );
  });
}

function handleCanDeactivateResult(
  result:
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree
): Observable<boolean | UrlTree> | boolean | UrlTree {
  if (typeof result === 'boolean') {
    return result ? true : showConfirmationDialog();
  }

  if (result instanceof UrlTree) {
    return result;
  }

  if (isObservable(result)) {
    return result.pipe(
      switchMap((value) => {
        if (typeof value === 'boolean') {
          return value ? of(true) : showConfirmationDialog();
        }

        return of(value);
      })
    );
  }

  return from(result).pipe(
    switchMap((value) => {
      if (typeof value === 'boolean') {
        return value ? of(true) : showConfirmationDialog();
      }

      return of(value);
    })
  );
}

function getFallbackCanDeactivateResult(component: AnyRecord): boolean | UrlTree {
  const bypassFlagKeys = [
    'skipCanDeactivate',
    'canDeactivateBypass',
    'isSaved',
    'saveSuccess',
    'saveSucceeded',
  ];

  for (const key of bypassFlagKeys) {
    if (isTruthyFlag(component, key)) {
      return true;
    }
  }

  const savingKeys = ['submitting', 'isSaving', 'saving'];
  for (const key of savingKeys) {
    if (isTruthyFlag(component, key)) {
      return true;
    }
  }

  if (typeof (component['spuFormWrapper'] as AnyRecord | undefined)?.['canDeactivate'] === 'function') {
    return (component['spuFormWrapper'] as { canDeactivate: () => boolean | UrlTree }).canDeactivate();
  }

  if (typeof (component['addFormComponent'] as AnyRecord | undefined)?.['canDeactivate'] === 'function') {
    return (component['addFormComponent'] as { canDeactivate: () => boolean | UrlTree }).canDeactivate();
  }

  const hasUnsavedChanges = component['hasUnsavedChanges'];
  if (typeof hasUnsavedChanges === 'function') {
    return !hasUnsavedChanges();
  }

  const form = getCandidateForm(component);
  if (typeof form?.['dirty'] === 'boolean') {
    return !form['dirty'];
  }

  return true;
}

export const CanDeactivateGuard: CanDeactivateFn<CanComponentDeactivate | object> = (
  component: CanComponentDeactivate | object,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState: RouterStateSnapshot
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  if (
    !component ||
    typeof (component as CanComponentDeactivate).canDeactivate !== 'function'
  ) {
    return handleCanDeactivateResult(getFallbackCanDeactivateResult(component as AnyRecord));
  }

  const canDeactive = (component as CanComponentDeactivate).canDeactivate(
    currentRoute,
    currentState,
    nextState
  );

  return handleCanDeactivateResult(canDeactive);
};
